/**
 * Contract helpers — exposes AgentMarketEscrow + AgentRegistry
 * instances bound to either a signer (writes) or a read-only provider.
 * Addresses are read from env at call time so .env changes pick up on
 * next render without a rebuild.
 */

import { Contract, type ContractRunner, type TransactionResponse } from "ethers";
import EscrowAbi from "./AgentMarketEscrow.abi.json";
import RegistryAbi from "./AgentRegistry.abi.json";
import { toUsdc } from "./usdc";

export { USDC_ABI, usdcContract, toUsdc, fromUsdc, getUSDCBalance, checkAllowance, approveUSDC } from "./usdc";
export { getChainConfig, switchToBotworkChain, txExplorerUrl } from "./network";

function requireEnv(name: string): string {
  const v = process.env[name];
  if (!v) throw new Error(`${name} is not set. Configure it in .env.local.`);
  return v;
}

export function getEscrowAddress(): string {
  return requireEnv("NEXT_PUBLIC_ESCROW_CONTRACT");
}

export function getRegistryAddress(): string {
  return requireEnv("NEXT_PUBLIC_REGISTRY_CONTRACT");
}

export function getEscrowContract(runner: ContractRunner): Contract {
  return new Contract(getEscrowAddress(), EscrowAbi as unknown as Record<string, unknown>[], runner);
}

export function getRegistryContract(runner: ContractRunner): Contract {
  return new Contract(
    getRegistryAddress(),
    RegistryAbi as unknown as Record<string, unknown>[],
    runner,
  );
}

/**
 * Derive a `bytes32` task id from the shell's string id. Contract
 * requires bytes32; we hash the string once on client side so the same
 * shell task id always maps to the same escrow key.
 */
export async function taskIdToBytes32(taskId: string): Promise<string> {
  const { id } = await import("ethers");
  return id(taskId);
}

/** Convenience wrapper — creates an escrow in USDC 6-dp from dollar input. */
export async function createEscrow(
  runner: ContractRunner,
  taskIdStr: string,
  agentAddress: string,
  dollars: number,
): Promise<TransactionResponse> {
  const escrow = getEscrowContract(runner);
  const taskId = await taskIdToBytes32(taskIdStr);
  const amount = toUsdc(dollars);
  return (await escrow.createTask(taskId, agentAddress, amount)) as TransactionResponse;
}

/** Convenience wrapper — completes the task (triggers 90/5/5 split). */
export async function completeEscrow(
  runner: ContractRunner,
  taskIdStr: string,
): Promise<TransactionResponse> {
  const escrow = getEscrowContract(runner);
  const taskId = await taskIdToBytes32(taskIdStr);
  return (await escrow.completeTask(taskId)) as TransactionResponse;
}

/** Convenience wrapper — disputes the task (funds locked until admin). */
export async function disputeEscrow(
  runner: ContractRunner,
  taskIdStr: string,
): Promise<TransactionResponse> {
  const escrow = getEscrowContract(runner);
  const taskId = await taskIdToBytes32(taskIdStr);
  return (await escrow.disputeTask(taskId)) as TransactionResponse;
}
